import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Ambulance, ArrowLeft, CheckCircle2, Clock3, AlertTriangle, FileText, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilePreview } from "@/context/FilePreviewContext";
import { getCurrentSignature, uploadSignature } from "@/lib/signatures";
import SignatureBlock from "@/components/SignatureBlock";

const BLURBS = {
  fwa: "Required by the payers we work with. Confirms you have read the policy and know how to report a concern.",
  ic: "The contract between you and the company: what you agree to do, how you are paid, and that you work as an independent contractor.",
};

const STATUS_META = {
  pending:  { label: "Not signed", cls: "text-slate-600 bg-slate-50 border-slate-200", Icon: FileText },
  signed:   { label: "Signed",     cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
  outdated: { label: "Needs resign", cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: AlertTriangle },
  expired:  { label: "Expired",    cls: "text-rose-700 bg-rose-50 border-rose-200", Icon: Clock3 },
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function normalizeName(s) {
  return (s || "").trim().replace(/\s+/g, " ").toLowerCase();
}

export default function DriverAgreements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("return");
  // Only honor a return param that points back into the driver portal, to avoid an open redirect.
  const validReturn = returnTo && returnTo.startsWith("/driver") ? returnTo : null;
  const backTo = validReturn || "/driver";
  const backLabel = validReturn ? "Back to onboarding" : "Back to rides";
  const { openPreview } = useFilePreview();
  const did = user?.driver_id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasSignature, setHasSignature] = useState(null); // null = unknown yet
  const [openedKeys, setOpenedKeys] = useState({});
  const [checked, setChecked] = useState({});
  const [typedName, setTypedName] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/drivers/${did}/agreements`),
      getCurrentSignature(did),
    ])
      .then(([r, sig]) => {
        setRows(r.data || []);
        setHasSignature(!!sig);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (did) load(); }, [did]); // eslint-disable-line react-hooks/exhaustive-deps

  const pendingCount = useMemo(() => rows.filter((r) => r.status !== "signed").length, [rows]);

  const openAgreement = async (row) => {
    openPreview({ kind: "agreement", id: row.key, meta: { label: row.title } });
    try {
      await api.post(`/drivers/${did}/agreements/${row.key}/opened`);
      setOpenedKeys((s) => ({ ...s, [row.key]: true }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const viewSignedCopy = (row) => {
    if (!row.doc_id) return;
    openPreview({ kind: "document", id: row.doc_id, meta: { label: `${row.title} (signed)` } });
  };

  const handleAdoptSignature = async ({ method, typedName: name, blob }) => {
    try {
      await uploadSignature(did, { method, typedName: name, blob });
      setHasSignature(true);
      toast.success("Signature saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sign = async (row) => {
    const name = (typedName[row.key] || "").trim();
    setBusyKey(row.key);
    try {
      await api.post(`/drivers/${did}/agreements/${row.key}/sign`, { typed_name: name, agreed: !!checked[row.key] });
      toast.success(`${row.title} signed`);
      setOpenedKeys((s) => ({ ...s, [row.key]: false }));
      setChecked((s) => ({ ...s, [row.key]: false }));
      setTypedName((s) => ({ ...s, [row.key]: "" }));
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "signature_required") {
        setHasSignature(false);
        toast.error("Add a signature below, then try signing again.");
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#005E82] flex items-center justify-center"><Ambulance size={18} /></div>
            <div>
              <div className="font-display font-semibold text-sm">Agreements</div>
              <div className="text-[11px] text-slate-400 truncate max-w-[180px]">{user?.name}</div>
            </div>
          </div>
          <button onClick={() => navigate(backTo)} className="min-h-[44px] text-slate-300 text-xs flex items-center gap-1.5 hover:text-white" data-testid="driver-agreements-back">
            <ArrowLeft size={14} /> {backLabel}
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 pt-5 pb-24" data-testid="driver-agreements">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}

        {!loading && rows.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center text-sm text-slate-500">
            Nothing to sign right now.
          </div>
        )}

        {!loading && pendingCount > 0 && (
          <div className="text-xs text-slate-500 mb-3">{pendingCount} agreement{pendingCount === 1 ? "" : "s"} need{pendingCount === 1 ? "s" : ""} your signature.</div>
        )}

        <div className="space-y-3">
          {rows.map((row) => {
            const meta = STATUS_META[row.status] || STATUS_META.pending;
            const opened = !!openedKeys[row.key];
            const isChecked = !!checked[row.key];
            const name = typedName[row.key] || "";
            const canSign = opened && isChecked && name.trim().length >= 2;
            const needsAction = row.status !== "signed";

            return (
              <article key={row.key} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`driver-agreement-${row.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-semibold text-slate-900">{row.title}</div>
                    <div className="text-xs text-slate-500 mt-1">{BLURBS[row.key] || "A custom agreement your company added."}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1 flex-shrink-0 text-[11px] font-medium rounded-full px-2 py-0.5 border ${meta.cls}`}>
                    <meta.Icon size={10} /> {meta.label}
                  </span>
                </div>

                {row.status === "signed" && (
                  <div className="mt-3 text-xs text-slate-500 space-y-1">
                    <div>Signed {fmtDate(row.signed_at)}{row.expires_at ? ` · expires ${fmtDate(row.expires_at)}` : ""}</div>
                    <button onClick={() => viewSignedCopy(row)} className="min-h-[36px] inline-flex items-center gap-1.5 text-[#005E82] hover:underline" data-testid={`agr-download-${row.key}`}>
                      <Download size={12} /> Download signed copy
                    </button>
                  </div>
                )}

                {row.status !== "signed" && (
                  <div className="mt-3 text-xs text-slate-500">
                    {row.status === "outdated" && "This agreement changed since you last signed it. Sign again to continue."}
                    {row.status === "expired" && `This signed copy expired ${fmtDate(row.expires_at)}. Sign again to continue.`}
                  </div>
                )}

                {needsAction && (
                  <div className="mt-3">
                    <button
                      onClick={() => openAgreement(row)}
                      className="min-h-[44px] w-full text-sm text-[#005E82] bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100"
                      data-testid={`agr-open-${row.key}`}
                    >
                      Open and read the agreement
                    </button>

                    <label className="flex items-start gap-2.5 mt-3 text-sm" style={{ opacity: opened ? 1 : 0.5 }}>
                      <input
                        type="checkbox"
                        disabled={!opened}
                        checked={isChecked}
                        onChange={(e) => setChecked((s) => ({ ...s, [row.key]: e.target.checked }))}
                        className="mt-0.5"
                        style={{ width: 18, height: 18 }}
                        data-testid={`agr-checkbox-${row.key}`}
                      />
                      <span>I have read the {row.title} in full and I agree to it.</span>
                    </label>

                    <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mt-3 block">Type your full legal name to sign</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setTypedName((s) => ({ ...s, [row.key]: e.target.value }))}
                      placeholder="Your full legal name"
                      disabled={!opened}
                      className="mt-1 w-full text-sm px-3 py-3 rounded-md border border-slate-200 disabled:bg-slate-50"
                      data-testid={`agr-name-${row.key}`}
                    />
                    <div className="text-[11px] text-slate-400 mt-1">
                      Signing records today's date. If you started work earlier, the agreement states that separately as its effective date.
                    </div>

                    {hasSignature === false ? (
                      <div className="mt-4 border-t border-slate-100 pt-4">
                        <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-2">Add a signature first</div>
                        <SignatureBlock legalName={user?.name} onAdopt={handleAdoptSignature} />
                      </div>
                    ) : (
                      <button
                        onClick={() => sign(row)}
                        disabled={!canSign || busyKey === row.key}
                        className="mt-3 min-h-[44px] w-full bg-[#0A192F] hover:bg-[#112240] disabled:opacity-50 text-white text-sm rounded-md font-semibold"
                        data-testid={`agr-sign-${row.key}`}
                      >
                        {busyKey === row.key ? "Signing…" : `Sign ${row.title}`}
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
